import { t } from 'i18next';
import * as yup from 'yup';

import { datasourceEndpoints, documentsEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { LENGTH_XS } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { ensureProtocol } from './helper';

const MINIO_UPLOAD_NAME_MAX_BYTES = 241;

const encoder = new TextEncoder();

/**
 * Yup test that validates the byte length of a string (not character length).
 * Filesystem filenames are limited to 255 bytes, and the server prepends a
 * 14-byte timestamp prefix, leaving 241 bytes for the name itself.
 * Multibyte characters (Greek, Chinese, etc.) consume 2-4 bytes each.
 */
const byteLengthTest: yup.TestConfig<string | undefined, yup.AnyObject> = {
  name: 'max-byte-length',
  message: ({ label }: { label?: string }) =>
    t('validation.field_too_long', { 0: label || t('common.title') }),
  test: (value) => {
    if (!value) {
      return true;
    }
    return encoder.encode(value).byteLength <= MINIO_UPLOAD_NAME_MAX_BYTES;
  },
};

const urlSchema = yup.string().url(() => t('validation.invalid_url'));
const normalizeUrl = (url?: string) =>
  ensureProtocol((url || '').trim())
    .replace(/\/$/, '')
    .toLowerCase();

async function validateUrlWithOrchestrator(
  url: string,
  context: yup.TestContext<yup.AnyObject>
): Promise<boolean | ReturnType<typeof context.createError>> {
  try {
    // Ensure URL has protocol before validation
    const urlWithProtocol = ensureProtocol(url);

    // Request to validate the url
    const result = await callGet(
      `/www/api/v1/collections/identify?url=${encodeURIComponent(urlWithProtocol)}`
    );

    // Handle errors
    if (result?.error_code) {
      return context.createError({ message: t(`errors.${result.error_code}`) });
    }

    // If the datasource is an Intercom or Zendesk knowledge base,
    // we need to inform the user so to use the External sources tab
    // instead of the Webpages tab
    if (result.datasource?.type === 'intercom_kb') {
      return context.createError({ message: t('collections.intercom_url') });
    }
    if (result.datasource?.type === 'zendesk_kb') {
      return context.createError({ message: t('collections.zendesk_url') });
    }
  } catch {
    return context.createError({ message: t('errors.invalidUrl') });
  }

  return true;
}

export const fullWebsiteSeedUrlSchema = yup
  .string()
  .transform((value) => ensureProtocol(value))
  .concat(urlSchema)
  .test({
    name: 'is valid url from orchestrator',
    test: validateUrlWithOrchestrator,
    message: () => t('validation.invalid_url'),
  });

// Onboarding-specific schema that validates URL format when value exists
// Note: The API call to identify the URL type (zendesk, intercom, etc.) is handled
// in the component (useAddKnowledge) after form submission, not during validation
export const onboardingWebsiteUrlSchema = yup
  .string()
  .transform((value) => {
    // Only transform if value is not empty
    if (!value || value.trim() === '') {
      return value;
    }
    return ensureProtocol(value);
  })
  .test({
    name: 'is-valid-url-if-provided',
    test: (value) => {
      // Allow empty values in onboarding where URL is optional
      if (!value || value.trim() === '') {
        return true;
      }
      // Validate URL format if value exists
      return yup.string().url().isValidSync(value);
    },
    message: () => t('validation.invalid_url'),
  });

export const addWebpagesSchema = yup.object().shape({
  method: yup.string().oneOf(['full_website', 'single_urls', 'sitemap']),
  seedUrl: yup
    .string()
    .when('method', {
      is: 'full_website',
      then: () => fullWebsiteSeedUrlSchema.clone().required(t('validation.required')),
      otherwise: () => yup.string().strip(),
    }),
  singleUrls: yup.array().when('method', {
    is: 'single_urls',
    then: () =>
      yup.array().of(
        yup
          .string()
          .transform((value) => ensureProtocol(value))
          .concat(urlSchema)
          .test({
            name: 'is valid url from orchestrator',
            test: async (url, context) => {
              return validateUrlWithOrchestrator(url, context);
            },
            message: t('validation.invalid_url'),
          })
          .required(t('validation.required'))
      ),
    otherwise: () => yup.array().strip(),
  }),
  sitemapUrl: yup.string().when('method', {
    is: 'sitemap',
    then: () =>
      yup
        .string()
        .transform((value) => ensureProtocol(value))
        .concat(urlSchema)
        .test({
          name: 'is valid url from orchestrator',
          test: async (url, context) => {
            return validateUrlWithOrchestrator(url, context);
          },
          message: t('validation.invalid_url'),
        })
        .required(t('validation.required')),
    otherwise: () => yup.string().strip(),
  }),
  excludedUrls: yup.array().when('method', {
    is: (val: string) => val === 'full_website' || val === 'sitemap',
    then: () =>
      yup
        .array()
        .of(
          yup
            .string()
            .transform((value) => ensureProtocol(value))
            .concat(urlSchema)
        )
        .test({
          name: 'excluded-does-not-match-seed',
          message: t('validation.url_cannot_be_excluded'),
          test: function (urls = []) {
            const { seedUrl, sitemapUrl } = this.parent;
            const includedUrls = [seedUrl, sitemapUrl].filter(Boolean).map(normalizeUrl);
            if (!includedUrls.length) {
              return true;
            }

            for (let i = 0; i < urls.length; i++) {
              const url = urls[i];
              if (!url) {
                continue;
              }
              const normalizedExcluded = normalizeUrl(url);
              if (includedUrls.includes(normalizedExcluded)) {
                return this.createError({
                  path: `${this.path}.${i}`,
                  message: t('validation.url_cannot_be_excluded'),
                });
              }
            }

            return true;
          },
        }),
    otherwise: () => yup.array().strip(),
  }),
  useProxy: yup.boolean().default(false),
  proxyCountry: yup.string().default(''),
});

export const addExternalSourcesSchema = (
  isEditing = false,
  nameCheck?: { collectionId: string; excludeName?: string }
) =>
  yup.object().shape({
    source: yup.string().required(t('validation.required')),
    subdomain: yup
      .string()
      .when('source', {
        is: 'zendesk',
        then: (schema) => schema.required(t('validation.required')),
        otherwise: (schema) => schema.strip(),
      }),
    categories: yup
      .array()
      .of(yup.string())
      .when('source', {
        is: 'zendesk',
        then: (schema) => schema,
        otherwise: (schema) => schema.strip(),
      }),
    language: yup
      .string()
      .when('source', {
        is: 'zendesk',
        then: (schema) => schema,
        otherwise: (schema) => schema.strip(),
      }),
    client_id: yup
      .string()
      .when('source', {
        is: 'workvivo',
        then: (schema) => schema.required(t('validation.required')),
        otherwise: (schema) => schema.strip(),
      }),
    access_token: yup
      .string()
      .when('source', {
        is: 'workvivo',
        then: (schema) => schema.required(t('validation.required')),
        otherwise: (schema) => schema.strip(),
      }),
    space_ids: yup
      .array()
      .of(yup.string())
      .when('source', {
        is: 'workvivo',
        then: (schema) => schema,
        otherwise: (schema) => schema.strip(),
      }),
    pages: yup
      .boolean()
      .when('source', {
        is: 'workvivo',
        then: (schema) => schema,
        otherwise: (schema) => schema.strip(),
      }),
    articles: yup
      .boolean()
      .when('source', {
        is: 'workvivo',
        then: (schema) => schema,
        otherwise: (schema) => schema.strip(),
      }),
    goals: yup
      .boolean()
      .when('source', {
        is: 'workvivo',
        then: (schema) => schema,
        otherwise: (schema) => schema.strip(),
      }),
    name: yup
      .string()
      .when('source', {
        is: 'gdrive',
        then: () =>
          createNameFieldSchema({
            getEndpoint: (name) =>
              datasourceEndpoints.checkName(nameCheck?.collectionId, name, {
                collection_id: nameCheck?.collectionId,
              }),
            max: LENGTH_XS,
            excludeName: nameCheck?.excludeName,
          }),
        otherwise: (schema) => schema.strip(),
      }),
    refresh_token: yup
      .string()
      .when('source', {
        is: 'gdrive',
        then: (schema) => (isEditing ? schema : schema.required()),
        otherwise: (schema) => schema.strip(),
      }),
    files: yup
      .array()
      .of(yup.object({ id: yup.string().required(), name: yup.string().required() }))
      .when('source', {
        is: 'gdrive',
        then: (schema) => schema.min(1).required(),
        otherwise: (schema) => schema.strip(),
      }),
    keep_published: isEditing ? yup.boolean().default(true) : yup.boolean().strip(),
  });

/**
 * Checks if a document name is available within a datasource on submit
 * This is used for submit-time validation, not real-time validation
 * Only applies to file-based documents (where file_url IS NOT NULL)
 *
 * @param params.collectionId - The collection ID
 * @param params.datasourceId - The datasource ID
 * @param params.name - The document name to check
 * @param params.documentId - The current document ID (when editing, to exclude from check)
 * @returns Promise<{ available: boolean; message?: string }>
 *
 * @example
 * // Check if document name is available when creating
 * const result = await checkDocumentNameAvailability({
 *   collectionId: 'col_123',
 *   datasourceId: 'ds_456',
 *   name: 'my-document.pdf'
 * });
 *
 * // Check if document name is available when editing
 * const result = await checkDocumentNameAvailability({
 *   collectionId: 'col_123',
 *   datasourceId: 'ds_456',
 *   name: 'my-document.pdf',
 *   documentId: 'doc_789' // exclude current document
 * });
 */
export async function checkDocumentNameAvailability(params: {
  collectionId: string;
  datasourceId: string;
  name: string;
  documentId?: string;
}): Promise<{ available: boolean; message?: string }> {
  const { collectionId, datasourceId, name, documentId } = params;

  try {
    const parentParams = documentId ? { document_id: documentId } : undefined;
    const endpoint = documentsEndpoints.checkName(collectionId, datasourceId, name, parentParams);

    const data = await callGet(endpoint);

    return {
      available: data.available ?? false,
      message: data.available ? undefined : 'This name is already taken',
    };
  } catch (error) {
    return { available: false, message: error?.message || 'Unable to verify name availability' };
  }
}

/**
 * Creates a yup test configuration for validating document name uniqueness
 *
 * @param collectionId - The collection ID
 * @param datasourceId - The datasource ID
 * @param documentId - Optional document ID to exclude from check (for edit mode)
 * @param excludeName - Optional name to exclude from uniqueness check (for edit mode, to skip validation if name unchanged)
 * @returns yup test configuration object
 */
function createUniqueNameTest(
  collectionId: string | undefined,
  datasourceId: string | undefined,
  documentId?: string,
  excludeName?: string
): yup.TestConfig<string, yup.AnyObject> {
  return {
    name: 'unique-name',
    message: () => t('validation.name_already_exists'),
    test: async (value, context) => {
      if (!value || !collectionId || !datasourceId) {
        return true;
      }

      // Skip validation if the name hasn't changed (edit mode)
      if (excludeName && value.trim().toLowerCase() === excludeName.trim().toLowerCase()) {
        return true;
      }

      try {
        const result = await checkDocumentNameAvailability({
          collectionId,
          datasourceId,
          name: value,
          documentId,
        });
        return result.available;
      } catch (error) {
        return context.createError({
          message: error?.message || t('validation.error_checking_name'),
        });
      }
    },
  };
}

export const editDocumentSchema = (
  collectionId: string,
  datasourceId: string,
  documentId?: string,
  excludeName?: string
) =>
  yup.object({
    title: yup
      .string()
      .label(t('collections.documents.edit_document.document_title'))
      .required(t('validation.required'))
      .test(byteLengthTest)
      .test(createUniqueNameTest(collectionId, datasourceId, documentId, excludeName)),
    externalUrl: yup.string().url(() => t('validation.invalid_url')),
  });

export const addTxtDocumentSchema = (
  collectionId: string,
  datasourceId: string,
  documentId?: string,
  excludeName?: string
) =>
  yup.object({
    title: yup
      .string()
      .label(t('common.title'))
      .required(t('validation.required'))
      .test(byteLengthTest)
      .test(createUniqueNameTest(collectionId, datasourceId, documentId, excludeName)),
    content: yup.string().required(t('validation.required')),
    externalUrl: yup.string().url(t('validation.invalid_url')),
    keep_published: yup.boolean(),
  });

export const addFAQSchema = (
  isEditing = false,
  collectionId?: string,
  datasourceId?: string,
  documentId?: string,
  excludeName?: string
) =>
  yup.object({
    question: yup
      .string()
      .test(byteLengthTest)
      .label(t('collections.add_faq.question'))
      .required(t('validation.required'))
      .test(
        createUniqueNameTest(
          collectionId,
          datasourceId,
          isEditing ? documentId : undefined,
          isEditing ? excludeName : undefined
        )
      ),
    answer: yup.string().label(t('collections.add_faq.answer')).required(t('validation.required')),
    keep_published: isEditing ? yup.boolean().default(true) : yup.boolean().strip(),
  });
